import { ClientError } from "graphql-request";

import type { Address, CartType, MagentoStoreConfig, SticksMatrix } from "@jti/fe-common";
import { customPaymentMethods, keysToCamelCase } from "@jti/fe-common";
import type {
  ApiBrandConsumablesResponse,
  ApiMagentoAddress,
  ApiMagentoCountryResponse,
  ApiMagentoCustomerCartPrices,
  ApiMagentoGetConsumablesMatrixResponse,
  ApiMagentoGetCurrencyResponse,
  ApiMagentoGetProductPriceResponse,
  ApiMagentoGetProductsDetails,
  ApiMagentoGetShippingMethods,
  ApiMagentoStoreConfigV2Response,
  MagentoCountry,
  MagentoCurrency,
  MagentoCustomerCartPrices,
  MagentoGetBrandConsumablesMatrixResponse,
  MagentoGetConsumablesMatrix,
  MagentoGetProductPrice,
  MagentoGetProductsDetails,
  MagentoPaymentMethod,
  MagentoSelectedShippingMethod,
  MagentoShippingMethod,
  MagentoStoreConfigV2,
} from "@jti/magento-types";
import { getUser, LoyaltyApi } from "@jti/rest-api";

import type {
  CartCoupon,
  IAddBundleProductsToCartMutation,
  IAddBundleProductsToCartMutationVariables,
  IAddConfigurableProductsToCartMutation,
  IAddConfigurableProductsToCartMutationVariables,
  IAddProductsToCartMutation,
  IAddProductsToCartMutationVariables,
  IAddSimpleProductsToCartMutation,
  IAddSimpleProductsToCartMutationVariables,
  IApplyCampaignToCartMutation,
  IApplyCampaignToCartMutationVariables,
  IApplyCouponsToCartMutation,
  IApplyCouponsToCartMutationVariables,
  IAvailablePaymentMethod,
  IBundleOptionInput,
  IBundleProductCartItemInput,
  ICancelOrderMutation,
  ICancelOrderMutationVariables,
  ICart,
  ICartAddressInput,
  ICartItemInput,
  ICreateEmptyCartMutation,
  ICreateEmptyCartMutationVariables,
  ICustomerAddressInput,
  ICustomerOrdersFilterInput,
  ICustomerOrderSortInput,
  IGetAvailablePaymentMethodsQuery,
  IGetAvailablePaymentMethodsQueryVariables,
  IGetBrandConsumablesMatrixQuery,
  IGetBrandConsumablesQuery,
  IGetCartIdQuery,
  IGetCartIdQueryVariables,
  IGetCartItemsQuery,
  IGetCartItemsQueryVariables,
  IGetCartPricesQuery,
  IGetCartPricesQueryVariables,
  IGetCartSelectedPaymentMethodQuery,
  IGetCartSelectedPaymentMethodQueryVariables,
  IGetCountiesForProfileQuery,
  IGetCountiesForProfileQueryVariables,
  IGetCountriesQuery,
  IGetCountriesQueryVariables,
  IGetCountryQuery,
  IGetCountryQueryVariables,
  IGetCustomerAddressesQuery,
  IGetCustomerAddressesQueryVariables,
  IGetCustomerCartQuery,
  IGetCustomerCartQueryVariables,
  IGetCustomerCouponsQuery,
  IGetCustomerCouponsQueryVariables,
  IGetCustomerOrderDetailsQuery,
  IGetCustomerOrderQuery,
  IGetCustomerOrderQueryVariables,
  IGetCustomerOrdersCountQuery,
  IGetCustomerOrdersPaginationQuery,
  IGetCustomerOrdersQuery,
  IGetCustomerOrdersQueryVariables,
  IGetCustomerOrdersStatusesQuery,
  IGetCustomerOrdersStatusesQueryVariables,
  IGetLastCustomerOrderQuery,
  IGetOrderAttributesQuery,
  IGetOrderDeliveryLimits,
  IGetOrderDeliveryLimitsQuery,
  IGetorderstatus,
  IGetOrderStatusesQuery,
  IGetProductImageDescriptionQuery,
  IGetProductImageDescriptionQueryVariables,
  IGetProductImageQuery,
  IGetProductImageQueryVariables,
  IGetProductsEssentialsQuery,
  IGetProductsQuery,
  IGetPromotionalOffersQuery,
  IGetSticksMatrixQuery,
  IGetStockStatusesQuery,
  IGetStoreConfigQuery,
  IGetUrlKeysQuery,
  IGetUrlKeysQueryVariables,
  IGetXSellingPopupQuery,
  IGetXSellingPopupQueryVariables,
  IMergeCartsMutation,
  IMergeCartsMutationVariables,
  IMoney,
  IOrderAttributeInput,
  IOrderTypeEnum,
  IPlaceOrderMutation,
  IPlaceOrderMutationVariables,
  IQueryGetProductPriceArgs,
  IRemoveCampaignFromCartMutation,
  IRemoveCampaignFromCartMutationVariables,
  IRemoveCouponsFromCartMutation,
  IRemoveCouponsFromCartMutationVariables,
  IRemoveItemFromCartMutation,
  IRemoveItemFromCartMutationVariables,
  IScopeTypeEnum,
  ISetAddressesOnCartMutation,
  ISetAddressesOnCartMutationVariables,
  ISetBillingAddressOnCartByAddressIdMutation,
  ISetBillingAddressOnCartByAddressIdMutationVariables,
  ISetBillingAddressOnCartMutation,
  ISetBillingAddressOnCartMutationVariables,
  ISetPaymentMethodOnCartMutation,
  ISetPaymentMethodOnCartMutationVariables,
  ISetShippingAddressesOnCartMutation,
  ISetShippingAddressesOnCartMutationVariables,
  ISetShippingMethodsOnCartMutation,
  ISetShippingMethodsOnCartMutationVariables,
  ISimpleProductCartItemInput,
  IUpdateCartItemQuantityMutation,
  IUpdateCartItemQuantityMutationVariables,
  IUpdateCustomerAddressMutation,
  IUpdateCustomerAddressMutationVariables,
  IUpdatePaymentMethodMutation,
  IUpdatePaymentMethodMutationVariables,
  IUpdatePaymentOutput,
  Scalars,
} from "../../types";
import {
  AddBundleProductsToCartDocument,
  AddConfigurableProductsToCartDocument,
  AddProductsToCartDocument,
  AddSimpleProductsToCartDocument,
  ApplyCampaignToCartDocument,
  ApplyCouponsToCartDocument,
  CancelOrderDocument,
  CreateEmptyCartDocument,
  GetAvailablePaymentMethodsDocument,
  GetBrandConsumablesDocument,
  GetBrandConsumablesMatrixDocument,
  GetCartIdDocument,
  GetCartItemsDocument,
  GetCartPricesDocument,
  GetCartSelectedPaymentMethodDocument,
  GetConsumablesMatrixDocument,
  GetCountriesDocument,
  GetCountryDocument,
  GetCurrencyDocument,
  GetCustomerAddressesDocument,
  GetCustomerCartDocument,
  GetCustomerCartIdDocument,
  GetCustomerCartPricesDocument,
  GetCustomerCouponsDocument,
  GetCustomerExportedOrderDetailsDocument,
  GetCustomerOrderDetailsDocument,
  GetCustomerOrderDocument,
  GetCustomerOrdersCountDocument,
  GetCustomerOrdersDocument,
  GetCustomerOrdersPaginationDocument,
  GetCustomerOrdersStatusesDocument,
  GetLastCustomerOrderDocument,
  GetOrderAttributesDocument,
  GetOrderDeliveryLimitsDocument,
  GetOrderStatusesDocument,
  GetProductImageDescriptionDocument,
  GetProductImageDocument,
  GetProductPriceDocument,
  GetProductsDocument,
  GetProductsEssentialsDocument,
  GetPromotionalOffersDocument,
  GetShippingMethodsDocument,
  GetSticksMatrixDocument,
  GetStockStatusesDocument,
  GetStoreConfigDocument,
  GetUrlKeysDocument,
  GetXSellingPopupDocument,
  MergeCartsDocument,
  PlaceOrderDocument,
  RemoveCampaignFromCartDocument,
  RemoveCouponsFromCartDocument,
  RemoveItemFromCartDocument,
  SetAddressesOnCartDocument,
  SetBillingAddressOnCartByAddressIdDocument,
  SetBillingAddressOnCartDocument,
  SetPaymentMethodOnCartDocument,
  SetShippingAddressesOnCartDocument,
  SetShippingMethodsOnCartDocument,
  UpdateCartItemQuantityDocument,
  UpdateCustomerAddressDocument,
  UpdatePaymentMethodDocument,
} from "../../types";
import type { Order } from "../../types/Order";
import type { FormTarget, OrderAttribute } from "../../types/OrderAttribute";
import type { OrderPrices } from "../../types/OrderPrices";
import type { Product, SelectedBundleOption } from "../../types/Product";
import { fromMagentoOrderAddress, normalizeProductNicotineStrengthData } from "../../utils";
import type { MagentoGraphQLRequestClient } from "../MagentoGraphQLRequestClient";

import { createCartItemData, isValidTarget, mapGetCartItemsLineToProduct } from "./utils";

// graphql-codegen v7 emits `Exact` as a non-exported local helper in CodegenTypes, so it is no
// longer re-exported from "../../types". Declared locally here (the only consumer) to keep parity.
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };

// TODO remove these types when Maybe and InputMaybe from codegen get fixed and are optional
type IFixedGetCustomerOrdersPaginationQueryVariables = Exact<{
  filter?: ICustomerOrdersFilterInput;
  currentPage?: Scalars["Int"];
  sort?: ICustomerOrderSortInput;
  scope?: IScopeTypeEnum;
  order_type?: IOrderTypeEnum;
}>;

type IFixedGetCustomerOrderDetailsQueryVariables = Exact<{
  number: Scalars["String"]["input"];
  scope?: IScopeTypeEnum;
}>;

type IFixedGetCustomerOrdersCountQueryVariables = Exact<{
  scope?: IScopeTypeEnum;
}>;

export class MagentoApiClient {
  constructor(
    private readonly client: MagentoGraphQLRequestClient,
    private clientWithGetRequestMethod: MagentoGraphQLRequestClient
  ) {}

  private static formatPrices({
    shipping_addresses = [],
    estimated_shipping_cost,
    prices: { grand_total, subtotal_excluding_tax, subtotal_including_tax, applied_taxes, discounts },
    total_shipping,
    applied_campaign,
    subscription_amount,
  }: Omit<
    IGetCartPricesQuery["cart"],
    "shipping_addresses" | "applied_coupons" | "minimum_payable_amount" | "subscription_amount"
  > & {
    shipping_addresses?: IGetCartPricesQuery["cart"]["shipping_addresses"];
    estimated_shipping_cost?: IGetCartPricesQuery["cart"]["estimated_shipping_cost"];
    total_shipping?: IMoney;
    subscription_amount?: number;
  }): OrderPrices {
    let shipping = undefined;
    if (shipping_addresses.length > 0) {
      const { selected_shipping_method } = shipping_addresses[0];
      shipping = selected_shipping_method;
    }
    if (total_shipping) {
      shipping = { amount: total_shipping };
    }

    // get currency from grand_total for subscription_amount, because subscription_amount is Float type
    let subscriptionAmount;
    if (subscription_amount) {
      subscriptionAmount = {
        value: subscription_amount,
        currency: grand_total.currency,
      };
    }

    return {
      grandTotal: grand_total,
      subtotal: subtotal_excluding_tax,
      subtotalIncludingTax: subtotal_including_tax,
      appliedTaxes: applied_taxes || [],
      discounts: discounts || [],
      shipping: shipping?.amount,
      estimatedShippingCost: estimated_shipping_cost?.amount,
      appliedCampaign: applied_campaign,
      ...(subscriptionAmount && { subscriptionAmount }),
    };
  }

  private fromMagentoPrices(args: Parameters<typeof MagentoApiClient.formatPrices>[0]): OrderPrices {
    return MagentoApiClient.formatPrices(args);
  }

  private static formatOrderAddress(
    address:
      | IGetCustomerOrderQuery["customer"]["orders"]["items"][0]["shipping_address"]
      | IGetCustomerOrderQuery["customer"]["orders"]["items"][0]["billing_address"]
  ): Address | null {
    return fromMagentoOrderAddress(address);
  }

  static fromMagentoOrder(
    order: NonNullable<IGetCustomerOrderQuery["customer"]["orders"]["items"][number]>,
    showPricesWithTaxExcluded: boolean = false
  ): Order {
    const {
      id,
      items: rawOrderItems,
      billing_address,
      shipping_address,
      carrier,
      number,
      order_date,
      shipping_method,
      shipping_method_code,
      status,
      loyalty_info,
      total: { discounts, grand_total, subtotal, subtotal_incl_tax, taxes, total_shipping },
      total_after_trial,
      total_including_trial,
      payment_methods,
      additional_attributes,
    } = order;

    const paymentMethod = payment_methods[0].type;

    const orderItems = (rawOrderItems || []).filter(Boolean) as any[];

    return {
      orderId: atob(id),
      paymentMethod,
      isZeroValueOrder: paymentMethod === customPaymentMethods.zeroValueOrder,
      items: orderItems.map((item) => {
        const {
          id: item_id,
          product_name: name,
          product_sku: sku,
          quantity_ordered: quantity,
          product_type,
          product,
          gift_data,
          row_total_incl_tax,
          row_total,
          product_sale_price: productSalePrice,
          product_sale_price_incl_tax: productSalePriceInclTax,
          trade_in,
        } = item;

        const { strength_details } = product;

        return {
          item_id,
          bundle_options: "bundle_options" in item ? (item.bundle_options as SelectedBundleOption[]) : undefined,
          sku,
          name,
          price: showPricesWithTaxExcluded ? row_total : row_total_incl_tax,
          productSalePrice,
          productSalePriceInclTax,
          quantity,
          isGift: gift_data?.is_gift || false,
          parentGiftSku: gift_data?.parent_gift_sku || "",
          imageUrl: product.image.url,
          categories: product.categories,
          product_type,
          price_range: (product as any).price_range,
          subscription: product.subscription,
          subscription_format: product.subscription_format,
          tradeIn: Boolean(trade_in),
          nicotineStrength: normalizeProductNicotineStrengthData(strength_details),
        };
      }),
      shippingAddress: MagentoApiClient.formatOrderAddress(shipping_address),
      billingAddress: MagentoApiClient.formatOrderAddress({ ...billing_address, pickup_information: "" }),
      number,
      orderDate: order_date,
      shippingMethod: {
        methodCode: shipping_method,
        carrierCode: carrier,
      },
      status,
      prices: MagentoApiClient.formatPrices({
        prices: {
          discounts,
          grand_total,
          subtotal_excluding_tax: subtotal,
          subtotal_including_tax: subtotal_incl_tax,
          applied_taxes: taxes.map(({ title, ...tax }) => ({
            ...tax,
            label: title,
          })),
        },
        total_shipping,
        applied_campaign: loyalty_info as any,
        available_payment_methods: [],
      } as any),
      total: { taxes, total_shipping },
      total_including_trial,
      total_after_trial,
      methodAndCarrierCode: shipping_method_code,
      additional_attributes,
    };
  }

  /**
   * @deprecated MagentoApiClient should not be responsible for its methods payload adaptation.
   * The methods accept already transformed data, which should be typed accordingly.
   */
  private toMagentoAddress(address: Address, saveInAddressBook = false): ApiMagentoAddress {
    const {
      isPickupAddress = false,
      pickupInformation,
      city,
      countryCode,
      streetName,
      streetNumber,
      streetAdditional,
      firstname,
      lastname,
      telephone,
      postcode,
      provinceName,
      districtName,
      premise,
      neighborhood,
      landmark,
      buildingNumber,
      district,
      region,
      regionCode,
      shortAddress,
    } = address;

    return {
      is_pickup_address: isPickupAddress,
      pickup_information: pickupInformation ? JSON.stringify(pickupInformation) : undefined,
      street: [streetName, streetNumber, streetAdditional],
      company: "",
      region: region?.region ?? "",
      region_id: region?.region_id ?? 0,
      city,
      country_code: countryCode,
      firstname,
      lastname,
      telephone,
      postcode,
      save_in_address_book: saveInAddressBook,
      administrative_area_level_2: provinceName,
      sublocality_level_2: districtName,
      premise,
      neighborhood,
      landmark,
      building_number: buildingNumber,
      district,
      region_code: regionCode ?? undefined,
      short_address: shortAddress,
    };
  }

  async getCustomerCart(): Promise<CartType> {
    const {
      customerCart: {
        id,
        free_shipping_threshold,
        is_trial_eligible,
        can_apply_coupons,
        applied_campaign,
        available_payment_methods,
      },
    } = await this.client.request<IGetCustomerCartQuery, IGetCustomerCartQueryVariables>(GetCustomerCartDocument);

    return {
      id,
      free_shipping_threshold,
      is_trial_eligible,
      can_apply_coupons,
      applied_campaign,
      available_payment_methods,
    };
  }

  async getCustomerCartId(): Promise<{ customerCart: { id: string } }> {
    return this.client.request<{ customerCart: { id: string } }>(GetCustomerCartIdDocument);
  }

  async checkCartAvailability(cartId: string): Promise<boolean> {
    try {
      const {
        cart: { id },
      } = await this.client.request<IGetCartIdQuery, IGetCartIdQueryVariables>(
        GetCartIdDocument,
        { cartId },
        { Authorization: "" }
      );

      return id !== null;
    } catch {
      return false;
    }
  }

  async mergeCarts(sourceCartId: string, destinationCartId: string): Promise<{ id: string }> {
    const {
      mergeCarts: { id },
    } = await this.client.request<IMergeCartsMutation, IMergeCartsMutationVariables>(MergeCartsDocument, {
      sourceCartId,
      destinationCartId,
    });

    return { id };
  }

  async createEmptyCart(): Promise<{ id: string }> {
    const { createEmptyCart } = await this.client.request<ICreateEmptyCartMutation, ICreateEmptyCartMutationVariables>(
      CreateEmptyCartDocument
    );

    return { id: createEmptyCart };
  }

  async getCartItems(
    cartId: string
  ): Promise<{ items: Product[]; availableFreeGifts: IGetCartItemsQuery["cart"]["available_free_gifts"] }> {
    let result = [];
    let available_free_gifts: IGetCartItemsQuery["cart"]["available_free_gifts"] = [];
    const user = getUser();
    try {
      const {
        cart: { items, available_free_gifts: cartAvailableFreeGifts },
      } = await this.client.request<IGetCartItemsQuery, IGetCartItemsQueryVariables>(GetCartItemsDocument, {
        cartId,
      });

      available_free_gifts = cartAvailableFreeGifts;

      const isLoyaltyActive = await LoyaltyApi.init().getStatus();

      if (!isLoyaltyActive) {
        return {
          items: items.map((item) => mapGetCartItemsLineToProduct(item)),
          availableFreeGifts: available_free_gifts,
        };
      }

      //@TODO create types for item DESP-41432
      result = await Promise.all(
        items.map((item: any) =>
          LoyaltyApi.init()
            .getProductPoints(item?.product?.sku, item.quantity, user?.uuid, user?.ciam_token)
            .then(({ points, rules }) => ({
              ...item,
              points,
              rules,
            }))
            .catch(() => {
              return item;
            })
        )
      );
    } catch (error: ClientError | any) {
      result = (error.response.data as IGetCartItemsQuery).cart.items;
    }

    return {
      items: result.filter(Boolean).map((item: any) => mapGetCartItemsLineToProduct(item, item.points, item.rules)),
      availableFreeGifts: available_free_gifts,
    };
  }

  async getCartPrices(cartId: string): Promise<{
    prices: OrderPrices;
    available_payment_methods: Pick<IAvailablePaymentMethod, "code" | "title">[];
    minimum_payable_amount: IMoney;
  }> {
    const { cart } = await this.client.request<IGetCartPricesQuery, IGetCartPricesQueryVariables>(
      GetCartPricesDocument,
      { cartId }
    );

    return {
      prices: this.fromMagentoPrices(cart),
      available_payment_methods: cart.available_payment_methods,
      minimum_payable_amount: cart.minimum_payable_amount,
    };
  }

  async getCustomerCartPrices(cartId: string): Promise<MagentoCustomerCartPrices> {
    const { cart } = await this.client.request<ApiMagentoCustomerCartPrices>(GetCustomerCartPricesDocument, { cartId });

    return {
      cart: keysToCamelCase(cart),
    };
  }

  async removeItemFromCart(
    cartId: string,
    productId: string
  ): Promise<{
    cart: Pick<ICart, "id">;
  }> {
    try {
      const { removeItemFromCart } = await this.client.request<
        IRemoveItemFromCartMutation,
        IRemoveItemFromCartMutationVariables
      >(RemoveItemFromCartDocument, {
        cartId,
        itemId: Number(productId),
      });
      return removeItemFromCart;
    } catch (error: ClientError | any) {
      const { removeItemFromCart } = error.data as IRemoveItemFromCartMutation;
      return removeItemFromCart;
    }
  }

  async updateCartItemQuantity(
    cartId: string,
    cartItemId: string,
    quantity: number
  ): Promise<{
    cart: Pick<ICart, "id">;
  }> {
    try {
      const { updateCartItems } = await this.client.request<
        IUpdateCartItemQuantityMutation,
        IUpdateCartItemQuantityMutationVariables
      >(UpdateCartItemQuantityDocument, {
        cartId,
        cartItemId: Number(cartItemId),
        quantity,
      });
      return updateCartItems;
    } catch (error: ClientError | any) {
      const { updateCartItems } = error.data as IUpdateCartItemQuantityMutation;
      return updateCartItems;
    }
  }

  /**
   * @deprecated use useSetShippingAddressOnCartMutation from @jti/store/slices/magentoApiSlice instead
   */

  async setShippingAddressOnCart(cartId: string, address: Address): Promise<{ id: string } | undefined> {
    const {
      setShippingAddressesOnCart: {
        cart: { id },
      },
    } = await this.client.request<ISetShippingAddressesOnCartMutation, ISetShippingAddressesOnCartMutationVariables>(
      SetShippingAddressesOnCartDocument,
      {
        cartId,
        address: this.toMagentoAddress(address) as unknown as ICartAddressInput, // TODO remove casting once Maybe is fixed (vat_id should be optional)
      }
    );
    return { id };
  }

  async getShippingMethods(
    cartId: string
  ): Promise<{ methods: MagentoShippingMethod[]; methodSelected: MagentoSelectedShippingMethod | null }> {
    const response = await this.client.request<ApiMagentoGetShippingMethods>(GetShippingMethodsDocument, {
      cartId,
    });

    const { shippingAddresses = [] } = keysToCamelCase(response.cart);

    if (shippingAddresses.length === 0) {
      return {
        methods: [],
        methodSelected: null,
      };
    }

    const { availableShippingMethods, selectedShippingMethod } = shippingAddresses[0];
    const availableShippingMethodsSortedAscending = availableShippingMethods.sort(
      (a, b) => a.amount.value - b.amount.value
    );

    return {
      methods: availableShippingMethodsSortedAscending,
      methodSelected: selectedShippingMethod || null,
    };
  }

  async setShippingMethodsOnCart(cartId: string, carrierCode: string, methodCode: string): Promise<{ id: string }> {
    const {
      setShippingMethodsOnCart: {
        cart: { id },
      },
    } = await this.client.request<ISetShippingMethodsOnCartMutation, ISetShippingMethodsOnCartMutationVariables>(
      SetShippingMethodsOnCartDocument,
      { cartId, carrierCode, methodCode }
    );
    return { id };
  }

  async getCountries(): Promise<{
    countries: {
      id: string;
      twoLettersAbbr: string;
      threeLettersAbbr: string;
      fullNameLocale: string;
      fullNameEnglish: string;
    }[];
  }> {
    const { countries } = await this.client.request<IGetCountriesQuery, IGetCountriesQueryVariables>(
      GetCountriesDocument
    );

    return {
      countries: countries.map(
        ({ id, two_letter_abbreviation, three_letter_abbreviation, full_name_locale, full_name_english }) => ({
          id,
          twoLettersAbbr: two_letter_abbreviation,
          threeLettersAbbr: three_letter_abbreviation,
          fullNameLocale: full_name_locale,
          fullNameEnglish: full_name_english,
        })
      ),
    };
  }

  async getCountriesForProfile(): Promise<IGetCountiesForProfileQuery> {
    return this.client.request<IGetCountiesForProfileQuery, IGetCountiesForProfileQueryVariables>(GetCountriesDocument);
  }

  async getCustomerOrder(orderNumber: string, showPricesWithTaxExcluded: boolean = false): Promise<Order> {
    const {
      customer: {
        orders: { items: orders },
      },
    } = await this.client.request<IGetCustomerOrderQuery, IGetCustomerOrderQueryVariables>(GetCustomerOrderDocument, {
      orderNumber,
    });

    const order = orders[0];

    if (!order) {
      throw new Error("ORDER_NOT_FOUND");
    }

    return MagentoApiClient.fromMagentoOrder(order, showPricesWithTaxExcluded);
  }

  async cancelCustomerOrder(orderNumber: string): Promise<{ success: boolean }> {
    const { cancelorder: result } = await this.client.request<ICancelOrderMutation, ICancelOrderMutationVariables>(
      CancelOrderDocument,
      { orderNumber }
    );
    return {
      success: result === true,
    };
  }

  async getCustomerAddresses(): Promise<IGetCustomerAddressesQuery> {
    return this.client.request<IGetCustomerAddressesQuery, IGetCustomerAddressesQueryVariables>(
      GetCustomerAddressesDocument
    );
  }

  async getCustomerOrdersCount(scope?: IScopeTypeEnum): Promise<IGetCustomerOrdersCountQuery> {
    return this.client.request<IGetCustomerOrdersCountQuery, IFixedGetCustomerOrdersCountQueryVariables>(
      GetCustomerOrdersCountDocument,
      { scope }
    );
  }

  async getCustomerOrders(filter?: ICustomerOrdersFilterInput): Promise<IGetCustomerOrdersQuery> {
    return this.client.request<IGetCustomerOrdersQuery, IGetCustomerOrdersQueryVariables>(
      GetCustomerOrdersDocument,
      filter ? { filter } : undefined
    );
  }

  async getCustomerOrdersStatuses(filter?: ICustomerOrdersFilterInput): Promise<IGetCustomerOrdersStatusesQuery> {
    return this.client.request<IGetCustomerOrdersStatusesQuery, IGetCustomerOrdersStatusesQueryVariables>(
      GetCustomerOrdersStatusesDocument,
      filter ? { filter } : undefined
    );
  }

  async getCustomerOrdersPagination(
    opts: IFixedGetCustomerOrdersPaginationQueryVariables
  ): Promise<IGetCustomerOrdersPaginationQuery> {
    return this.client.request<IGetCustomerOrdersPaginationQuery, IFixedGetCustomerOrdersPaginationQueryVariables>(
      GetCustomerOrdersPaginationDocument,
      { ...opts }
    );
  }

  async getOrderAttributes(): Promise<OrderAttribute[]> {
    const { getOrderAttributes: orderAttributes } =
      await this.client.request<IGetOrderAttributesQuery>(GetOrderAttributesDocument);

    const formattedOrderAttributes: OrderAttribute[] = orderAttributes
      .filter((attribute) => isValidTarget(attribute.placement))
      .map((attribute) => ({
        ...attribute,
        placement: attribute.placement as FormTarget,
      }));

    return formattedOrderAttributes;
  }

  async getCustomerOrderDetails(orderNumber: string, scope?: IScopeTypeEnum): Promise<IGetCustomerOrderDetailsQuery> {
    return this.client.request<IGetCustomerOrderDetailsQuery, IFixedGetCustomerOrderDetailsQueryVariables>(
      GetCustomerOrderDetailsDocument,
      { number: orderNumber, scope }
    );
  }

  async getCustomerExportOrderDetails(orderNumber: string): Promise<IGetCustomerOrderDetailsQuery> {
    return this.client.request<IGetCustomerOrderDetailsQuery, IFixedGetCustomerOrderDetailsQueryVariables>(
      GetCustomerExportedOrderDetailsDocument,
      { number: orderNumber }
    );
  }

  async getOrderStatuses(): Promise<{ getorderstatus: IGetorderstatus[] }> {
    const { getorderstatus: getOrderStatus } =
      await this.client.request<IGetOrderStatusesQuery>(GetOrderStatusesDocument);

    const translatedStatuses = getOrderStatus.map(({ status_label, status_value, ...status }) => {
      return {
        ...status,
        status_label: status_label ?? "",
        status_value,
      };
    });

    return { getorderstatus: translatedStatuses };
  }

  async updateCustomerAddress(id: number, address: ICustomerAddressInput): Promise<IUpdateCustomerAddressMutation> {
    return this.client.request<IUpdateCustomerAddressMutation, IUpdateCustomerAddressMutationVariables>(
      UpdateCustomerAddressDocument,
      { addressId: id, address }
    );
  }

  async getProductImage(sku: string): Promise<IGetProductImageQuery> {
    return this.clientWithGetRequestMethod.request<IGetProductImageQuery, IGetProductImageQueryVariables>(
      GetProductImageDocument,
      {
        sku,
      }
    );
  }

  async getProductImageDescription(
    skuArr: string[]
  ): Promise<{ products: { sku: string; description: string; imageUrl: string }[] }> {
    const {
      products: { items },
    } = await this.client.request<IGetProductImageDescriptionQuery, IGetProductImageDescriptionQueryVariables>(
      GetProductImageDescriptionDocument,
      { sku: skuArr }
    );
    return {
      products: items.map(({ sku, description: { html: description }, image: { url: imageUrl } }) => ({
        sku,
        description,
        imageUrl,
      })),
    };
  }

  async getDefaultShippingMethods(
    cartId: string
  ): Promise<{ methods: MagentoShippingMethod[]; methodSelected: MagentoSelectedShippingMethod | null }> {
    return this.getShippingMethods(cartId);
  }

  async setBillingAddressOnCartByAddressId(
    cartId: string,
    customerAddressId: number
  ): Promise<{ id: string } | undefined> {
    try {
      const {
        setBillingAddressOnCart: {
          cart: { id },
        },
      } = await this.client.request<
        ISetBillingAddressOnCartByAddressIdMutation,
        ISetBillingAddressOnCartByAddressIdMutationVariables
      >(SetBillingAddressOnCartByAddressIdDocument, {
        cartId,
        customer_address_id: customerAddressId,
      });

      return { id };
    } catch (error: ClientError | any) {
      const { response } = error;
      throw new Error(response.errors[0].message);
    }
  }

  async setBillingAddressOnCart(cartId: string, address: Address): Promise<{ id: string } | undefined> {
    const {
      setBillingAddressOnCart: {
        cart: { id },
      },
    } = await this.client.request<ISetBillingAddressOnCartMutation, ISetBillingAddressOnCartMutationVariables>(
      SetBillingAddressOnCartDocument,
      {
        cartId,
        address: this.toMagentoAddress(address) as unknown as ICartAddressInput, // TODO remove casting once Maybe is fixed (vat_id should be optional)
      }
    );

    return { id };
  }

  async setAddressesOnCart(
    cartId: string,
    shipping_address_input: Address,
    billing_address_input: Address
  ): Promise<{ id: string } | undefined> {
    try {
      const {
        setBillingAddressOnCart: {
          cart: { id },
        },
      } = await this.client.request<ISetAddressesOnCartMutation, ISetAddressesOnCartMutationVariables>(
        SetAddressesOnCartDocument,
        {
          cartId,
          shipping_addresses: this.toMagentoAddress(shipping_address_input) as unknown as ICartAddressInput,
          billing_address: this.toMagentoAddress(billing_address_input) as unknown as ICartAddressInput,
        }
      );
      return { id };
    } catch (error: ClientError | any) {
      throw new ClientError(error.response, error.request);
    }
  }

  async setPaymentMethodOnCart(cartId: string, code: string): Promise<ISetPaymentMethodOnCartMutation> {
    return this.client.request<ISetPaymentMethodOnCartMutation, ISetPaymentMethodOnCartMutationVariables>(
      SetPaymentMethodOnCartDocument,
      {
        cartId,
        code,
      }
    );
  }

  async getLastCustomerOrder(): Promise<IGetLastCustomerOrderQuery> {
    return this.client.request<IGetLastCustomerOrderQuery>(GetLastCustomerOrderDocument);
  }

  async getSelectedPaymentMethod(cartId: string): Promise<IGetCartSelectedPaymentMethodQuery> {
    return this.client.request<IGetCartSelectedPaymentMethodQuery, IGetCartSelectedPaymentMethodQueryVariables>(
      GetCartSelectedPaymentMethodDocument,
      {
        cartId,
      }
    );
  }

  /**
   * @deprecated use placeOrderV2 instead
   */
  async LEGACY__placeOrder(
    cartId: string,
    additionalAttributes: IOrderAttributeInput[],
    paymentMethod = "jtipayment"
  ): Promise<{
    orderNumber: string;
    entityId: string;
    errors: Array<{ message: string; code: string }>;
  }> {
    await this.setPaymentMethodOnCart(cartId, paymentMethod);
    const { placeOrder } = await this.client.request<IPlaceOrderMutation, IPlaceOrderMutationVariables>(
      PlaceOrderDocument,
      {
        cartId,
        additionalAttributes,
      }
    );

    return {
      orderNumber: placeOrder?.orderV2?.number ?? "",
      entityId: placeOrder?.orderV2?.id ? atob(placeOrder.orderV2.id) : "",
      errors: placeOrder?.errors?.filter(Boolean) ?? [],
    };
  }

  async getCustomerCoupons(cartId: string): Promise<{
    coupons: CartCoupon[];
    canApplyCoupons: boolean;
  }> {
    const {
      cart: { applied_coupons: coupons, can_apply_coupons },
    } = await this.client.request<IGetCustomerCouponsQuery, IGetCustomerCouponsQueryVariables>(
      GetCustomerCouponsDocument,
      { cartId }
    );

    return { coupons, canApplyCoupons: can_apply_coupons };
  }

  async applyCouponsToCart(cartId: string, couponCodes: string[]): Promise<IApplyCouponsToCartMutation> {
    return this.client.request<IApplyCouponsToCartMutation, IApplyCouponsToCartMutationVariables>(
      ApplyCouponsToCartDocument,
      {
        cartId,
        couponCodes,
      }
    );
  }

  async removeCouponsFromCart(cartId: string, couponCodes: string[]): Promise<IRemoveCouponsFromCartMutation> {
    return this.client.request<IRemoveCouponsFromCartMutation, IRemoveCouponsFromCartMutationVariables>(
      RemoveCouponsFromCartDocument,
      {
        cartId,
        couponCodes,
      }
    );
  }

  async applyCampaignToCart(cartId: string, campaign_uuid: string): Promise<IApplyCampaignToCartMutation> {
    return this.client.request<IApplyCampaignToCartMutation, IApplyCampaignToCartMutationVariables>(
      ApplyCampaignToCartDocument,
      {
        cartId,
        campaign_uuid,
      }
    );
  }

  async removeCampaignFromCart(cartId: string): Promise<IRemoveCampaignFromCartMutation> {
    return this.client.request<IRemoveCampaignFromCartMutation, IRemoveCampaignFromCartMutationVariables>(
      RemoveCampaignFromCartDocument,
      {
        cartId,
      }
    );
  }

  async addProductsToCart(
    cartId: string,
    items: Array<{ quantity: number; productSku: string; giftSku: string; parentGiftId?: string }>
  ): Promise<IAddProductsToCartMutation | null> {
    const nonEmptyItems = items.filter(({ quantity }) => quantity > 0);
    const cartItems = nonEmptyItems.map(({ quantity, productSku, giftSku, parentGiftId }) => {
      const entered_options = [
        { uid: btoa("custom-option/is_gift"), value: "true" },
        { uid: btoa("custom-option/parent_gift_sku"), value: productSku },
        parentGiftId ? { uid: btoa("custom-option/parent_gift_id"), value: parentGiftId } : undefined,
      ].filter(Boolean);

      return {
        quantity,
        sku: giftSku,
        entered_options,
      };
    }) as ICartItemInput[];

    if (cartItems.length === 0) {
      return Promise.resolve(null);
    }

    return this.client.request<IAddProductsToCartMutation, IAddProductsToCartMutationVariables>(
      AddProductsToCartDocument,
      {
        cartId,
        cartItems,
      }
    );
  }

  async addPromoGiftToCart(
    cartId: string,
    items: Array<{ sku: string; quantity: number; salesRuleId: number; selectedOptions?: string[] }>
  ): Promise<IAddProductsToCartMutation | null> {
    const nonEmptyItems = items.filter(({ quantity }) => quantity > 0);
    const cartItems = nonEmptyItems.map(({ sku, quantity, salesRuleId, selectedOptions }) => {
      const entered_options = [
        { uid: btoa("custom-option/is_gift"), value: "true" },
        { uid: btoa("custom-option/is_salesrule_gift"), value: "true" },
        { uid: btoa("custom-option/gift_salesrule_id"), value: String(salesRuleId) },
      ];

      return {
        quantity,
        sku,
        entered_options,
        ...(selectedOptions?.length && { selected_options: selectedOptions }),
      };
    }) as ICartItemInput[];

    if (cartItems.length === 0) {
      return Promise.resolve(null);
    }

    return this.client.request<IAddProductsToCartMutation, IAddProductsToCartMutationVariables>(
      AddProductsToCartDocument,
      {
        cartId,
        cartItems,
      }
    );
  }

  async addSimpleProductsToCart(
    cartId: string,
    sku: string,
    quantity = 1,
    isConsentGiven?: boolean
  ): Promise<IAddSimpleProductsToCartMutation> {
    const data = createCartItemData(sku, quantity, isConsentGiven);

    // TODO remove casting once codegen types for cart payload are configured with Maybe as optional
    const cartItem = { data } as ISimpleProductCartItemInput;

    return this.client.request<IAddSimpleProductsToCartMutation, IAddSimpleProductsToCartMutationVariables>(
      AddSimpleProductsToCartDocument,
      {
        cartId,
        cartItems: [cartItem],
      }
    );
  }

  async addConfigurableProductToCart(
    cartId: string,
    sku: string,
    parentSku: string,
    quantity = 1
  ): Promise<IAddConfigurableProductsToCartMutation> {
    return this.client.request<IAddConfigurableProductsToCartMutation, IAddConfigurableProductsToCartMutationVariables>(
      AddConfigurableProductsToCartDocument,
      {
        cartId,
        sku,
        parentSku,
        quantity,
      }
    );
  }

  async addBundleProductsToCart(
    cartId: string,
    sku: string,
    bundleOptions: IBundleOptionInput[],
    quantity = 1,
    isConsentGiven?: boolean
  ): Promise<IAddBundleProductsToCartMutation> {
    const data = createCartItemData(sku, quantity, isConsentGiven);

    // TODO remove casting once codegen types for cart payload are configured with Maybe as optional
    const cartItem = { bundle_options: bundleOptions, data } as IBundleProductCartItemInput;

    return this.client.request<IAddBundleProductsToCartMutation, IAddBundleProductsToCartMutationVariables>(
      AddBundleProductsToCartDocument,
      {
        cartId,
        cartItems: [cartItem],
      }
    );
  }

  async removeItemsFromCart(
    cartId: string,
    productIds: string[]
  ): Promise<IRemoveItemFromCartMutation["removeItemFromCart"][]> {
    return Promise.all<IRemoveItemFromCartMutation["removeItemFromCart"]>(
      productIds.map((productId) => this.removeItemFromCart(cartId, productId))
    );
  }

  async getCurrency(): Promise<MagentoCurrency> {
    const { currency } = await this.clientWithGetRequestMethod.request<
      ApiMagentoGetCurrencyResponse,
      IGetCartItemsQueryVariables
    >(GetCurrencyDocument);

    return keysToCamelCase(currency);
  }

  async getStoreConfig(): Promise<MagentoStoreConfig> {
    const {
      storeConfig: {
        store_code,
        store_group_code,
        website_code,
        is_order_limit_enabled,
        is_age_verification_enabled,
        age_verification_mode,
        is_free_gifts_enabled,
        gift_basket_quantity_limitation,
        order_limit_value,
        base_currency_code,
        postcode_is_required,
        postcode_max_length,
        postcode_min_length,
        postcode_validate_regex,
        postcode_validation_enabled,
        allow_empty_firstname,
        cartons_limit_value,
      },
    } = await this.clientWithGetRequestMethod.request<IGetStoreConfigQuery>(GetStoreConfigDocument);

    return {
      store_code,
      store_group_code,
      website_code,
      is_order_limit_enabled,
      is_age_verification_enabled,
      age_verification_mode,
      is_free_gifts_enabled,
      gift_basket_quantity_limitation,
      order_limit_value,
      base_currency_code,
      postcode_is_required,
      postcode_max_length,
      postcode_min_length,
      postcode_validate_regex,
      postcode_validation_enabled,
      allow_empty_firstname,
      cartons_limit_value,
    };
  }

  async getStoreConfigV2(): Promise<MagentoStoreConfigV2> {
    const response =
      await this.clientWithGetRequestMethod.request<ApiMagentoStoreConfigV2Response>(GetStoreConfigDocument);
    return keysToCamelCase(response).storeConfig;
  }

  async getOrderDeliveryLimits(): Promise<IGetOrderDeliveryLimits> {
    const {
      getOrderDeliveryLimits: { limits, mode },
    } = await this.client.request<IGetOrderDeliveryLimitsQuery>(GetOrderDeliveryLimitsDocument);

    return {
      limits,
      mode,
    };
  }

  async updatePaymentMethod(orderId: string, methodCode: string): Promise<IUpdatePaymentOutput> {
    try {
      const { updatePaymentMethod } = await this.client.request<
        IUpdatePaymentMethodMutation,
        IUpdatePaymentMethodMutationVariables
      >(UpdatePaymentMethodDocument, {
        orderId,
        methodCode,
      });

      return updatePaymentMethod;
    } catch (error: ClientError | any) {
      const { updatePaymentMethod } = error.data as IUpdatePaymentMethodMutation;
      return updatePaymentMethod;
    }
  }

  async updatePaymentMethodV2(orderId: string, methodCode: string): Promise<IUpdatePaymentMethodMutation> {
    return await this.client.request<IUpdatePaymentMethodMutation, IUpdatePaymentMethodMutationVariables>(
      UpdatePaymentMethodDocument,
      {
        orderId,
        methodCode,
      }
    );
  }

  async getProductsDetailsBySku(sku: string[], pageSize: number): Promise<IGetProductsQuery> {
    return this.client.request<IGetProductsQuery>(GetProductsDocument, {
      sku,
      pageSize,
    });
  }

  async getProductsEssentialsBySku(sku: string[], pageSize: number): Promise<IGetProductsEssentialsQuery> {
    return this.client.request<IGetProductsEssentialsQuery>(GetProductsEssentialsDocument, {
      sku,
      pageSize,
    });
  }

  async getPromotionalOffersBySku(sku: string[], pageSize: number): Promise<IGetPromotionalOffersQuery> {
    return this.client.request<IGetPromotionalOffersQuery>(GetPromotionalOffersDocument, {
      sku,
      pageSize,
    });
  }
  async CUSTOM_TYPES_BETA__getProductsDetailsBySku(
    skus: string[],
    pageSize: number
  ): Promise<MagentoGetProductsDetails> {
    const response = await this.client.request<ApiMagentoGetProductsDetails>(GetProductsDocument, {
      sku: skus,
      pageSize,
    });

    return keysToCamelCase(response);
  }

  async getStockStatuses(sku: string[], pageSize: number): Promise<IGetStockStatusesQuery> {
    return this.client.request<IGetStockStatusesQuery>(GetStockStatusesDocument, {
      sku,
      pageSize,
    });
  }

  async getProductPrice(
    sku: IQueryGetProductPriceArgs["sku"],
    quantity: IQueryGetProductPriceArgs["quantity"]
  ): Promise<MagentoGetProductPrice> {
    const response = await this.client.request<ApiMagentoGetProductPriceResponse>(GetProductPriceDocument, {
      sku,
      quantity,
    });

    return keysToCamelCase(response.getProductPrice);
  }

  async getSticksMatrix(): Promise<SticksMatrix> {
    const response = await this.client.request<IGetSticksMatrixQuery>(GetSticksMatrixDocument);

    return keysToCamelCase(response.getSticksMatrix);
  }

  async getBrandConsumablesMatrix(brandCode = "all"): Promise<MagentoGetBrandConsumablesMatrixResponse> {
    const response = await this.client.request<IGetBrandConsumablesMatrixQuery>(GetBrandConsumablesMatrixDocument, {
      brandCode,
    });

    return keysToCamelCase(response.getBrandConsumableMatrix);
  }

  async getBrandConsumables(brandCode: string): Promise<ApiBrandConsumablesResponse> {
    const response = await this.client.request<IGetBrandConsumablesQuery>(GetBrandConsumablesDocument, {
      brandCode,
    });

    return keysToCamelCase(response.getBrandConsumableMatrix) as unknown as ApiBrandConsumablesResponse;
  }

  async getConsumablesMatrix(): Promise<MagentoGetConsumablesMatrix> {
    const response = await this.client.request<ApiMagentoGetConsumablesMatrixResponse>(GetConsumablesMatrixDocument);

    return keysToCamelCase(response.getConsumablesMatrix);
  }

  /**
   * @deprecated use getCountryV2 instead
   */
  async getCountry(id: string): Promise<IGetCountryQuery> {
    return this.client.request<IGetCountryQuery, IGetCountryQueryVariables>(GetCountryDocument, {
      id,
    });
  }

  async getCountryV2(id: string): Promise<MagentoCountry> {
    const result = await this.client.request<ApiMagentoCountryResponse, IGetCountryQueryVariables>(GetCountryDocument, {
      id,
    });

    return keysToCamelCase(result.country);
  }

  async getXSellingPopup(cartId: string): Promise<IGetXSellingPopupQuery> {
    return this.client.request<IGetXSellingPopupQuery, IGetXSellingPopupQueryVariables>(GetXSellingPopupDocument, {
      cartId,
    });
  }

  async getUrlKeys(sku: string): Promise<IGetUrlKeysQuery> {
    return this.client.request<IGetUrlKeysQuery, IGetUrlKeysQueryVariables>(GetUrlKeysDocument, {
      sku,
    });
  }

  async getAvailablePaymentMethods(cartId: string): Promise<MagentoPaymentMethod[]> {
    const response = await this.client.request<
      IGetAvailablePaymentMethodsQuery,
      IGetAvailablePaymentMethodsQueryVariables
    >(GetAvailablePaymentMethodsDocument, { cartId });

    return keysToCamelCase(response.cart.available_payment_methods);
  }
}
